import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import SwitchableButtonBar from '../../../../../common/components/switchableButtonBar/SwitchableButtonBar';
import EngageTextInputField from '../../../../../common/components/form-elements/textInputField/TextInputField';
import { QUESTION_CHARACTER_LIMIT } from '../../../../../common/constants/QuestionConstants';
import EngageCheckboxInputField from '../../../../../common/components/form-elements/checkboxInputField/CheckboxInputField';
import EngageCheckboxGroupInputField from '../../../../../common/components/form-elements/checkboxGroupInputField/CheckboxGroupInputField';
import {
  prepareFeedbackQuestionConfigFormValues,
  prepareFeedbackQuestionPersonalInfoGroupData,
  prepareFeedbackQuestionSubmitConfig,
  FEEDBACK_QUESTION_PERSONAL_INFO_PREFIX,
  FEEDBACK_QUESTION_ALLOW_IMAGE_FIELD,
  FEEDBACK_QUESTION_COLLECT_EMAIL_ONLY,
  FEEDBACK_QUESTION_COLLECT_POSTCODE_ONLY,
  FEEDBACK_QUESTION_PERSONAL_INFO_EMAIL_FIELD,
  FEEDBACK_QUESTION_PERSONAL_INFO_POST_CODE_FIELD,
} from '../../PollUtils';
import { getFormFieldClass, getFormFieldTranslationTooltip } from '../../../../../common/TranslationUtils';
import ConditionalLogic from '../../../../../common/components/conditionalLogic/ConditionalLogic';
import ConditionalLogicUtil from '../../../../../common/ConditionalLogicUtil';

function FeedbackQuestion(props) {
  const { question, updateQuestion, removeQuestion, openPollPreview, showConditionalLogic, Store } = props;

  const [choices, setChoices] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [rules, setRules] = useState([]);

  const [isEmailOnly, setIsEmailOnly] = useState();
  const [isPostCodeOnly, setIsPostCodeOnly] = useState();

  useEffect(() => {
    if (!question) return;

    const initialFormQuestionValues = {
      questionText: question.questionText,
    };

    setIsEmailOnly(question.questionConfiguration?.collectEmailOnly);
    setIsPostCodeOnly(question.questionConfiguration?.collectPostCodeOnly);

    const configurationValues = prepareFeedbackQuestionConfigFormValues(question, initialFormQuestionValues);
    const prepChoices = prepareFeedbackQuestionPersonalInfoGroupData(question, configurationValues);
    setChoices(prepChoices);

    const initialState = ConditionalLogicUtil.setupFormInitialValues(question, initialFormQuestionValues);
    setFormValues({ ...initialFormQuestionValues, ...initialState.initialFormConditionValues });
    setRules(initialState.initialRules);
  }, [question]);

  const onSubmit = (values) => {
    const updatedQuestionData = toJS(question);
    // Question content
    updatedQuestionData.questionText = values.questionText;

    prepareFeedbackQuestionSubmitConfig(updatedQuestionData, values);

    ConditionalLogicUtil.extractConditionalFormValues(question, updatedQuestionData, values, rules);

    updateQuestion(updatedQuestionData);
  };

  // The "collect X only" modes own the image and mandatory-info toggles: switching one on
  // resets them to the values the client app expects, and switches the other mode off.
  function getFeedbackConfigFields(state) {
    return {
      allowImagesField: state.fields[FEEDBACK_QUESTION_ALLOW_IMAGE_FIELD],
      requireEmailField:
        state.fields[
          `${FEEDBACK_QUESTION_PERSONAL_INFO_PREFIX}-${FEEDBACK_QUESTION_PERSONAL_INFO_EMAIL_FIELD}-${question._id}`
        ],
      requirePostcodeField:
        state.fields[
          `${FEEDBACK_QUESTION_PERSONAL_INFO_PREFIX}-${FEEDBACK_QUESTION_PERSONAL_INFO_POST_CODE_FIELD}-${question._id}`
        ],
      emailOnlyField: state.fields[FEEDBACK_QUESTION_COLLECT_EMAIL_ONLY],
      postCodeOnlyField: state.fields[FEEDBACK_QUESTION_COLLECT_POSTCODE_ONLY],
    };
  }

  function emailOnlyChangeHandler(args, state) {
    const emailOnly = state.formState.values[FEEDBACK_QUESTION_COLLECT_EMAIL_ONLY];
    setIsEmailOnly(emailOnly);

    if (emailOnly) {
      const { allowImagesField, requireEmailField, requirePostcodeField, postCodeOnlyField } =
        getFeedbackConfigFields(state);
      allowImagesField.change(false);
      requireEmailField.change(false);
      requirePostcodeField.change(false);
      postCodeOnlyField.change(false);
      setIsPostCodeOnly(false);
    }
  }

  function postCodeOnlyChangeHandler(args, state) {
    const postCodeOnly = state.formState.values[FEEDBACK_QUESTION_COLLECT_POSTCODE_ONLY];
    setIsPostCodeOnly(postCodeOnly);

    if (postCodeOnly) {
      const { allowImagesField, requireEmailField, requirePostcodeField, emailOnlyField } =
        getFeedbackConfigFields(state);
      allowImagesField.change(false);
      // postcode is the only thing collected, so it must be required and email must not be
      requireEmailField.change(false);
      requirePostcodeField.change(true);
      emailOnlyField.change(false);
      setIsEmailOnly(false);
    }
  }

  const isCollectOnlyMode = isEmailOnly || isPostCodeOnly;

  return (
    <Form
      onSubmit={onSubmit}
      keepDirtyOnReinitialize={true}
      initialValues={formValues}
      mutators={{ emailOnlyChangeHandler, postCodeOnlyChangeHandler }}
      render={({ values, handleSubmit, form }) => {
        return (
          <form className='c-fields l-form' onSubmit={(event) => event.preventDefault()}>
            <EngageTextInputField
              id={`input-question-${question._id}`}
              placeholder='Enter question'
              label='Question'
              maxLength={QUESTION_CHARACTER_LIMIT}
              fieldDescription='Ask users to provide additional feedback. If people decide to write feedback (up to 2000 characters)
                they will have to provide an e-mail address. This question is best placed at the end of the survey.'
              fieldName='questionText'
              isRequired={true}
              fieldClass={getFormFieldClass(question.defaultData.questionText, null, 'l-form__item')}
              tooltipInLabel={getFormFieldTranslationTooltip(question.defaultData.questionText)}
            />

            <div className='c-toggle-content'>
              <div className='c-toggle-content__container'>
                <ConditionalLogic
                  formValues={values}
                  setFormValues={setFormValues}
                  question={question}
                  questions={Store.poll.questions}
                  rules={rules}
                  setRules={setRules}
                  show={showConditionalLogic}
                  choiceFieldNames={[]}
                />
                <div className='c-toggle-content__content' hidden={showConditionalLogic}>
                  <div className='c-field l-form__item'>
                    <EngageCheckboxInputField
                      id={`checkbox-allow-images-${question._id}`}
                      type='checkbox'
                      fieldName={FEEDBACK_QUESTION_ALLOW_IMAGE_FIELD}
                      fieldClass='l-form__item'
                      label='Image upload'
                      labelTooltip='Users will be able to attach an image with their feedback.'
                      isToggle={true}
                      checkedLabel='Enable images'
                      uncheckedLabel='Enable images'
                      hideLabel={false}
                      disabled={isCollectOnlyMode}
                    />
                    <EngageCheckboxGroupInputField
                      choices={choices}
                      label='Mandatory personal information'
                      fieldName={FEEDBACK_QUESTION_PERSONAL_INFO_PREFIX}
                      isToggle={true}
                      disabled={isCollectOnlyMode}
                    />
                    <div className='l-form'>
                      <EngageCheckboxInputField
                        id={`checkbox-email-only-${question._id}`}
                        type='checkbox'
                        fieldName={FEEDBACK_QUESTION_COLLECT_EMAIL_ONLY}
                        fieldClass='l-form__item--m'
                        label='Collect email only'
                        labelTooltip='If checked users will only be able to provide email but not additional feedback.'
                        isToggle={true}
                        checkedLabel='Yes'
                        uncheckedLabel='No'
                        hideLabel={false}
                        onSelectionChange={form.mutators.emailOnlyChangeHandler}
                      />
                      <EngageCheckboxInputField
                        id={`checkbox-postcode-only-${question._id}`}
                        type='checkbox'
                        fieldName={FEEDBACK_QUESTION_COLLECT_POSTCODE_ONLY}
                        fieldClass='l-form__item--m'
                        label='Collect postcode only'
                        labelTooltip='If checked users will only be able to provide their postcode but not additional feedback.'
                        isToggle={true}
                        checkedLabel='Yes'
                        uncheckedLabel='No'
                        hideLabel={false}
                        onSelectionChange={form.mutators.postCodeOnlyChangeHandler}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <SwitchableButtonBar
              updateAction={handleSubmit}
              removeAction={removeQuestion}
              isNew={null}
              previewAction={() => openPollPreview(question._id)}
              updateEnabled={props.updateEnabled}
            />
          </form>
        );
      }}
    />
  );
}

FeedbackQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  updateQuestion: PropTypes.func.isRequired,
  removeQuestion: PropTypes.func.isRequired,
  openPollPreview: PropTypes.func.isRequired,
  showConditionalLogic: PropTypes.bool.isRequired,
  Store: PropTypes.object.isRequired,
};

export default observer(FeedbackQuestion);
